import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../constants";

export function useRulesEngine() {
  const [activeRules, setActiveRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/business-rules?is_active=true`)
      .then(res => setActiveRules(Array.isArray(res.data) ? res.data : []))
      .catch(() => setActiveRules([]))
      .finally(() => setLoading(false));
  }, []);

  /**
   * Evalúa si se puede añadir un servicio de una categoría dada,
   * según las reglas REQUIERE_CATEGORIA activas.
   * @param {string} categoria - La categoría del servicio que se quiere añadir
   * @param {Array<{catalog_service_id: string, categoria: string}>} currentServices
   * @returns {{ permitido: boolean, mensaje: string|null }}
   */
  const checkRequiresCategory = useCallback((categoria, currentServices) => {
    if (!categoria) return { permitido: true, mensaje: null };
    const rules = activeRules.filter(r =>
      r.tipo === "REQUIERE_CATEGORIA" && r.categoria_condicion === categoria
    );
    for (const rule of rules) {
      const hasRequired = currentServices.some(s => s.categoria === rule.categoria_requerida);
      if (!hasRequired) {
        return { permitido: false, mensaje: rule.mensaje_error };
      }
    }
    return { permitido: true, mensaje: null };
  }, [activeRules]);

  /**
   * Filtra las opciones de un dropdown según reglas EXCLUSIVIDAD.
   * Si el presupuesto actual contiene condicion_producto_id,
   * devuelve solo [producto_requerido_id].
   * UNIDIRECCIONAL: no filtra al seleccionar producto_requerido.
   * @param {Array} opciones - Array de productos del catálogo (con .id)
   * @param {Array<{catalog_service_id: string}>} currentServices
   * @returns {Array} opciones filtradas
   */
  const filterByExclusivity = useCallback((opciones, currentServices) => {
    for (const rule of activeRules.filter(r => r.tipo === "EXCLUSIVIDAD")) {
      const hasCondicion = currentServices.some(s =>
        s.catalog_service_id === rule.condicion_producto_id
      );
      if (hasCondicion) {
        return opciones.filter(o => o.id === rule.producto_requerido_id);
      }
    }
    return opciones;
  }, [activeRules]);

  /**
   * Comprueba si un descuento está disponible según DESCUENTO_CONDICIONAL.
   * @param {string} discountProductId - ID del producto de descuento
   * @param {Array<{catalog_service_id: string}>} currentServices
   * @returns {{ disponible: boolean, mensaje: string|null }}
   */
  const checkDiscountAvailable = useCallback((discountProductId, currentServices) => {
    if (!discountProductId) return { disponible: true, mensaje: null };
    const rule = activeRules.find(r =>
      r.tipo === "DESCUENTO_CONDICIONAL" && r.condicion_producto_id === discountProductId
    );
    if (!rule) return { disponible: true, mensaje: null };
    const hasRequired = currentServices.some(s =>
      s.catalog_service_id === rule.producto_requerido_id
    );
    return {
      disponible: hasRequired,
      mensaje: hasRequired ? rule.mensaje_sugerencia : rule.mensaje_error,
    };
  }, [activeRules]);

  /*
   * Count-based eligibility for conditional discounts.
   * Generic, rule-driven, zero hardcoding.
   *  - requiredMatches: services in currentServices matching the rule
   *    requirement (producto_requerido_id or categoria_requerida).
   *  - pairedMatches: caller-supplied list of services the discount is
   *    meant to apply to (e.g. selected TVs for DESCUENTO_TV).
   *  - eligibleCount = min(requiredMatches.length, pairedMatches.length).
   * If the discount has no associated rule, it is unconditional and the
   * eligible count mirrors pairedMatches (falling back to 1).
   */
  const getDiscountEligibility = useCallback(
    (discountProduct, currentServices, { pairedMatches = [] } = {}) => {
      if (!discountProduct?.id) {
        return { disponible: false, eligibleCount: 0, requiredMatches: [], pairedMatches, mensaje: null };
      }
      const rule = activeRules.find(
        r => r.tipo === "DESCUENTO_CONDICIONAL" && r.condicion_producto_id === discountProduct.id
      );
      if (!rule) {
        const count = pairedMatches.length || 1;
        return { disponible: true, eligibleCount: count, requiredMatches: [], pairedMatches, mensaje: null };
      }
      const requiredMatches = currentServices.filter(s => {
        if (rule.producto_requerido_id) return s.catalog_service_id === rule.producto_requerido_id;
        if (rule.categoria_requerida)   return s.categoria === rule.categoria_requerida;
        return false;
      });
      const eligibleCount = Math.min(requiredMatches.length, pairedMatches.length);
      return {
        disponible: eligibleCount > 0,
        eligibleCount,
        requiredMatches,
        pairedMatches,
        mensaje: eligibleCount > 0 ? rule.mensaje_sugerencia : rule.mensaje_error,
      };
    },
    [activeRules]
  );

  /*
   * Final validator against the complete selected-services set.
   * Iterates every active rule (currently EXCLUSIVIDAD) and returns all
   * violations. Directional by design: only raised when `condicion_producto_id`
   * is present AND `producto_requerido_id` is missing. No hardcoded product
   * names, IDs or categories — all references come from the rule record.
   */
  const validateCurrentServices = useCallback((currentServices) => {
    const violations = [];
    const list = Array.isArray(currentServices) ? currentServices : [];
    activeRules.forEach(rule => {
      if (rule.tipo !== "EXCLUSIVIDAD") return;
      if (!rule.condicion_producto_id || !rule.producto_requerido_id) return;
      const hasCondition = list.some(s => s.catalog_service_id === rule.condicion_producto_id);
      if (!hasCondition) return;
      const hasRequired = list.some(s => s.catalog_service_id === rule.producto_requerido_id);
      if (hasRequired) return;
      const fallbackMsg = `"${rule.condicion_producto_nombre || "Este producto"}" requiere "${rule.producto_requerido_nombre || "el producto asociado"}"`;
      violations.push({
        ruleId: rule.id,
        tipo: rule.tipo,
        message: rule.mensaje_error || fallbackMsg,
        condicion_producto_id: rule.condicion_producto_id,
        condicion_producto_nombre: rule.condicion_producto_nombre,
        producto_requerido_id: rule.producto_requerido_id,
        producto_requerido_nombre: rule.producto_requerido_nombre,
      });
    });
    return { ok: violations.length === 0, violations };
  }, [activeRules]);

  return { activeRules, loading, checkRequiresCategory, filterByExclusivity, checkDiscountAvailable, getDiscountEligibility, validateCurrentServices };
}
